import { useEffect, useState } from 'react';
import { Card as BootstrapCard } from 'react-bootstrap';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { projectUserRole, showDropdown } from 'redux/projectSlice';
import { useGetPaginatedInspectionsMutation } from 'redux/services/inspection';
import { RootStates } from 'redux/store';
import { ItemCard } from './ItemCard';
import { IInspection } from 'redux/conductInspectionSlice';
import { Loader } from 'components/common/Loader';
import { useGetChecklist } from 'hooks/useGetChecklist';
import { useTranslation } from 'react-i18next';
import { StartInspectionModal } from './StartInspectionModal';
import { RescheduleInspectionModal } from './RescheduleInspectionModal';
import { DeleteInspectionModal } from './DeleteInspectionModal';
import { Status } from 'components/common/WorkFlowHeader/WorkFlowHeader';
import './ScheduledInspections.scss';
import { Pagination } from '@mui/material';

const ScheduledInspections: React.FC = () => {
  const { t } = useTranslation();
  const [selectedInspection, setSelectedInspection] =
    useState<IInspection | null>(null);
  const [showCancelInspection, setShowCancelInspection] = useState(false);
  const [showRescheduleInspection, setShowRescheduleInspection] =
    useState(false);
  const [showStartInspection, setShowStartInspection] = useState(false);

  const { projectId } = useParams();
  const dispatch = useDispatch();
  const [getAllInspections, { data: response, isLoading }] =
    useGetPaginatedInspectionsMutation();
  const project = useSelector((state: RootStates) =>
    state.project.projects.find((item) => item.projectInfo.id === projectId),
  );
  useGetChecklist();

  useEffect(() => {
    if (project) {
      dispatch(projectUserRole(project.role));
    }
  }, [dispatch, project]);

  useEffect(() => {
    getAllInspections({
      id: projectId,
      status: Status.Scheduled,
    });
    dispatch(showDropdown(true));
    return () => {
      dispatch(showDropdown(false));
    };
  }, [dispatch, getAllInspections, projectId]);

  const getStartInspectionHandler = (inspection: IInspection) => () => {
    setShowStartInspection(true);
    setSelectedInspection(inspection);
  };
  const getDeleteInspectionHandler = (inspection: IInspection) => () => {
    setSelectedInspection(inspection);
    setShowCancelInspection(true);
  };
  const getRescheduleInspectionHandler = (inspection: IInspection) => () => {
    setSelectedInspection(inspection);
    setShowRescheduleInspection(true);
  };

  const handleCloseCancelInspection = () => {
    setShowCancelInspection(false);
    setSelectedInspection(null);
  };

  const handleCloseRescheduleInspection = () => {
    setShowRescheduleInspection(false);
    setSelectedInspection(null);
  };

  const handleCloseStartInspection = () => {
    setShowStartInspection(false);
    setSelectedInspection(null);
  };

  const handleSuccess = () => {
    getAllInspections({
      id: projectId,
      status: Status.Scheduled,
    });
  };

  const handlePaginationChange = (
    _: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (Number(response?.pagination?.page) !== value) {
      getAllInspections({
        id: projectId,
        page: value,
        status: Status.Scheduled,
      });
    }
  };

  return (
    <div className="scheduled-inspections d-flex flex-column h-100">
      <div className="page-title">
        <h2 className="page-title">{t('header_scheduled_inspections')}</h2>
      </div>
      <BootstrapCard className="flex-grow-1">
        <BootstrapCard.Body className="d-flex flex-column flex-md-row flex-wrap gap-3 pageBg">
          {((response?.data as IInspection[]) ?? [])?.map(
            (item: IInspection) => (
              <ItemCard
                key={item._id}
                date={
                  item.scheduledDate
                    ? moment(item.scheduledDate).format('DD/MM/YYYY hh:mm A')
                    : ''
                }
                title={project?.projectInfo.projectName ?? ''}
                doer={
                  project?.team?.find((teamObj) => teamObj.userId === item.doer)
                    ?.name ?? ''
                }
                inspector={
                  project?.team?.find(
                    (teamObj) => teamObj.userId === item.inspector,
                  )?.name ?? ''
                }
                projects={Object.values({
                  subProject: item.subProject?.name ?? '',
                  subTask: item.subTask?.name ?? '',
                  endTask: item.endTask?.name ?? '',
                }).filter((value) => value)}
                locations={item.locations}
                onDelete={getDeleteInspectionHandler(item)}
                onReschedule={getRescheduleInspectionHandler(item)}
                onStart={getStartInspectionHandler(item)}
              />
            ),
          )}
          {isLoading && <Loader />}
          {!response?.data.length && (
            <div className="d-flex w-100 h-100 flex-column p-2 align-items-center justify-content-center">
              <div className="no-scheduled-inspections-img" />
              <span className="h6">{t('no_scheduled_inspections')}</span>
            </div>
          )}
        </BootstrapCard.Body>
      </BootstrapCard>
      <Pagination
        className="align-self-end my-1"
        count={response?.pagination?.totalPage ?? 0}
        page={Number(response?.pagination?.page ?? 1)}
        color="primary"
        shape="rounded"
        onChange={handlePaginationChange}
      />
      {!!selectedInspection && showStartInspection && (
        <StartInspectionModal
          inspection={selectedInspection}
          open={showStartInspection}
          onClose={handleCloseStartInspection}
        />
      )}

      <RescheduleInspectionModal
        inspection={selectedInspection}
        open={showRescheduleInspection}
        onClose={handleCloseRescheduleInspection}
        onSuccess={handleSuccess}
      />
      <DeleteInspectionModal
        inspectionId={selectedInspection?._id ?? ''}
        open={showCancelInspection}
        onClose={handleCloseCancelInspection}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default ScheduledInspections;
